/**
 * RichTextEditor — minimal contentEditable-based rich text editor with a
 * compact toolbar (Bold / Italic / Underline / Strikethrough). Stored HTML
 * is sanitized with DOMPurify on save. Body is resizable via CSS ``resize:
 * vertical`` and grows/scrolls internally for long content.
 *
 * Props:
 *   value          — initial HTML string (or plain text; renders inline)
 *   onChange(html) — fires with sanitized HTML on every input
 *   placeholder    — greyed placeholder when empty
 *   minHeight      — px, default 120
 *   testId
 */
import { useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import { TextB, TextItalic, TextUnderline, TextStrikethrough } from "@phosphor-icons/react";

const ALLOWED_TAGS = ["b", "strong", "i", "em", "u", "s", "strike", "br", "p", "div", "span"];
const ALLOWED_ATTR = [];

export function sanitizeNoteHtml(html) {
  if (!html) return "";
  return DOMPurify.sanitize(String(html), { ALLOWED_TAGS, ALLOWED_ATTR });
}

export default function RichTextEditor({ value = "", onChange, placeholder = "", minHeight = 120, testId }) {
  const ref = useRef(null);

  // Set innerHTML only on initial mount / when caller resets the field.
  useEffect(() => {
    if (!ref.current) return;
    const clean = sanitizeNoteHtml(value);
    if (ref.current.innerHTML !== clean) {
      ref.current.innerHTML = clean;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value === "" ? "reset" : "keep"]);

  const exec = (cmd) => {
    ref.current?.focus();
    // execCommand is deprecated but works reliably across all evergreen
    // browsers for these four basic formatters and requires no dep.
    document.execCommand(cmd, false, null);
    handleInput();
  };
  const handleInput = () => {
    if (!ref.current) return;
    const html = sanitizeNoteHtml(ref.current.innerHTML);
    onChange?.(html);
  };

  return (
    <div className="rounded-md border border-slate-300 bg-white overflow-hidden" data-testid={testId}>
      <div className="flex items-center gap-0.5 border-b border-slate-200 bg-slate-50 px-1 py-0.5">
        <ToolBtn onClick={() => exec("bold")} title="Bold (Ctrl+B)" testId={`${testId}-bold`}><TextB size={13} weight="bold" /></ToolBtn>
        <ToolBtn onClick={() => exec("italic")} title="Italic (Ctrl+I)" testId={`${testId}-italic`}><TextItalic size={13} weight="bold" /></ToolBtn>
        <ToolBtn onClick={() => exec("underline")} title="Underline (Ctrl+U)" testId={`${testId}-underline`}><TextUnderline size={13} weight="bold" /></ToolBtn>
        <ToolBtn onClick={() => exec("strikethrough")} title="Strikethrough" testId={`${testId}-strike`}><TextStrikethrough size={13} weight="bold" /></ToolBtn>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onBlur={handleInput}
        data-placeholder={placeholder}
        className="px-2 py-1.5 text-sm text-slate-800 focus:outline-none empty:before:content-[attr(data-placeholder)] empty:before:text-slate-400"
        style={{ minHeight, maxHeight: "60vh", overflowY: "auto", resize: "vertical" }}
        data-testid={`${testId}-content`}
      />
    </div>
  );
}

function ToolBtn({ onClick, title, children, testId }) {
  return (
    <button type="button" onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      title={title} data-testid={testId}
      className="h-6 w-6 inline-flex items-center justify-center text-slate-600 hover:text-[#1f64c2] hover:bg-white rounded">
      {children}
    </button>
  );
}
